import type {
  VirtualIpCharacterBatch,
  VirtualIpCharacterSelectionWorkspace,
  VirtualIpDraftStatus,
} from '../../api'

/** Per-step dependency outcome — separate from navigation/viewing state. */
export type VirtualIpStepDependencyState =
  | 'LOCKED'
  | 'AVAILABLE'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'NEEDS_REGENERATION'
  | 'FAILED'

export type VirtualIpStepStaleCause =
  | 'POSITIONING_REVISION'
  | 'SELECTION_LINEAGE'
  | 'UPSTREAM_STEP2'
  | 'UPSTREAM_STEP3'
  | 'UPSTREAM_STEP4'

export interface VirtualIpCharacterLineageFacts {
  hasSuccessfulCharacterCandidates: boolean
  hasAnyCharacterArtifact: boolean
  characterGenerationInFlight: boolean
  hasCharacterSelection: boolean
  /** Final selection matches current positioning revision lineage. */
  characterSelectionCurrent: boolean
  /** Unified: upstream revision or stale selection invalidates character step. */
  characterNeedsRegeneration: boolean
  maxSuccessfulBatchRevision: number
}

const IN_FLIGHT = new Set(['QUEUED', 'DISPATCHED', 'RUNNING', 'WAITING_PROVIDER', 'RESULT_RECEIVED'])

function batchHasUsableCandidate(batch: Pick<VirtualIpCharacterBatch, 'status' | 'candidates'>): boolean {
  if (batch.status !== 'SUCCEEDED') return false
  return (batch.candidates ?? []).some(
    (c) => Boolean(c.asset_id || c.thumbnail_url || c.download_url || c.canonical_asset_reference),
  )
}

function collectCharacterBatches(input: {
  selectionWorkspace?: VirtualIpCharacterSelectionWorkspace | null
  latestBatch?: VirtualIpCharacterBatch | null
}): VirtualIpCharacterBatch[] {
  const fromSelection = input.selectionWorkspace?.batches ?? []
  if (fromSelection.length > 0) return fromSelection
  return input.latestBatch ? [input.latestBatch] : []
}

/**
 * Legacy batches may have positioning_profile_revision = 0 before lineage was stamped.
 * Treat 0 as "unknown lineage" — do not false-positive NEEDS_REGENERATION.
 */
export function revisionNeedsRegeneration(
  artifactRevision: number | undefined,
  currentPositioningRevision: number,
): boolean {
  if (currentPositioningRevision <= 0) return false
  const stamped = artifactRevision ?? 0
  if (stamped <= 0) return false
  return stamped < currentPositioningRevision
}

function resolveSelectionSourceRevision(
  selectionWorkspace: VirtualIpCharacterSelectionWorkspace | null | undefined,
): number | null {
  const selection = selectionWorkspace?.selection
  if (!selection?.selected_id) return null

  if (selection.selected_kind === 'refinement') {
    const version = selectionWorkspace?.refinement_versions?.find(
      (v) => v.version_id === selection.selected_id,
    )
    if (!version) return null
    const rootBatch = selectionWorkspace?.batches?.find((b) =>
      (b.candidates ?? []).some((c) => c.candidate_id === version.lineage_root_id),
    )
    return rootBatch?.positioning_profile_revision ?? null
  }

  for (const batch of selectionWorkspace?.batches ?? []) {
    const hit = (batch.candidates ?? []).some(
      (c) => c.candidate_id === selection.selected_id || c.asset_id === selection.selected_id,
    )
    if (hit) return batch.positioning_profile_revision ?? null
  }
  return null
}

export function deriveCharacterLineageFacts(input: {
  positioningProfileRevision?: number
  selectionWorkspace?: VirtualIpCharacterSelectionWorkspace | null
  latestBatch?: VirtualIpCharacterBatch | null
}): VirtualIpCharacterLineageFacts {
  const batches = collectCharacterBatches(input)
  const currentRevision = input.positioningProfileRevision ?? 0
  const successfulBatches = batches.filter((b) => batchHasUsableCandidate(b))
  const maxSuccessfulBatchRevision = successfulBatches.reduce(
    (max, b) => Math.max(max, b.positioning_profile_revision ?? 0),
    0,
  )

  const batchesStale =
    successfulBatches.length > 0 &&
    maxSuccessfulBatchRevision > 0 &&
    revisionNeedsRegeneration(maxSuccessfulBatchRevision, currentRevision)

  const selection = input.selectionWorkspace?.selection
  const hasCharacterSelection = Boolean(selection?.selected_id)
  let characterSelectionCurrent = false

  if (hasCharacterSelection) {
    const sourceRevision = resolveSelectionSourceRevision(input.selectionWorkspace)
    if (sourceRevision == null) {
      characterSelectionCurrent = !batchesStale
    } else {
      characterSelectionCurrent = !revisionNeedsRegeneration(sourceRevision, currentRevision)
    }
  }

  const characterNeedsRegeneration = batchesStale || (hasCharacterSelection && !characterSelectionCurrent)

  return {
    hasSuccessfulCharacterCandidates: successfulBatches.length > 0,
    hasAnyCharacterArtifact: batches.length > 0 || Boolean(input.latestBatch),
    characterGenerationInFlight:
      batches.some((b) => IN_FLIGHT.has(b.status)) ||
      Boolean(input.latestBatch && IN_FLIGHT.has(input.latestBatch.status)),
    hasCharacterSelection,
    characterSelectionCurrent,
    characterNeedsRegeneration,
    maxSuccessfulBatchRevision,
  }
}

export function virtualIpStepRegenerationReasonKey(
  step: number,
  cause: VirtualIpStepStaleCause,
): string {
  switch (step) {
    case 3:
      return 'regeneration.step3TypeChanged'
    case 4:
      return 'regeneration.step4ProposalChanged'
    case 5:
      return cause === 'UPSTREAM_STEP4' || cause === 'POSITIONING_REVISION'
        ? 'regeneration.step5PositioningChanged'
        : 'regeneration.step5Expired'
    case 6:
      return 'regeneration.step6UpstreamChanged'
    case 7:
      return 'regeneration.step7UpstreamChanged'
    default:
      return 'regeneration.default'
  }
}

/** @deprecated Use virtualIpStepRegenerationReasonKey with i18n at render time. */
export function virtualIpStepRegenerationReason(step: number, cause: VirtualIpStepStaleCause): string {
  return virtualIpStepRegenerationReasonKey(step, cause)
}

export function draftCompletedThrough(
  status: VirtualIpDraftStatus,
  positioningConfirmedAt?: string | null,
): number {
  if (status === 'positioning_confirmed' || positioningConfirmedAt) return 4
  switch (status) {
    case 'step_3_completed':
      return 3
    case 'step_2_completed':
      return 2
    case 'step_1_completed':
      return 1
    default:
      return 0
  }
}
